import React from 'react';
import Form from 'react-bootstrap/Form';
import Select from 'react-select';
import CustomDatePicker from '../FormComponents/CustomDatePicker';
import { FilterOperator, ReportFieldMetadata } from '../../types/report.types';

interface Props {
  field: ReportFieldMetadata | undefined;
  operator: FilterOperator;
  value: string;
  onChange: (value: string) => void;
}

function FilterValueInput({ field, operator, value, onChange }: Props) {
  // IsEmpty and IsNotEmpty don't need a value input
  if (operator === 'IsEmpty' || operator === 'IsNotEmpty') {
    return <Form.Control type="text" value="" disabled placeholder="No value needed" />;
  }

  if (!field) {
    return <Form.Control type="text" value={value} onChange={(e) => onChange(e.target.value)} />;
  }

  switch (field.valueType) {
    case 'Boolean':
      return (
        <Form.Select value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">Select...</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </Form.Select>
      );

    case 'Date':
      return (
        <CustomDatePicker
          value={value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        />
      );

    case 'Dropdown': {
      const options = field.dropdownOptions || [];
      // Use searchable select for dropdowns with many options (like Client)
      if (options.length > 20) {
        const selectOptions = options.map((opt) => ({
          value: opt.value,
          label: opt.label
        }));
        const selectedOption = selectOptions.find((opt) => opt.value === value) || null;
        return (
          <Select
            options={selectOptions}
            value={selectedOption}
            onChange={(selected) => onChange(selected?.value || '')}
            isClearable
            isSearchable
            placeholder="Search..."
            styles={{
              control: (base) => ({
                ...base,
                minHeight: '38px'
              })
            }}
          />
        );
      }
      // Use regular select for small dropdowns
      return (
        <Form.Select value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">Select...</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      );
    }

    case 'Number':
      return (
        <Form.Control
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter number"
        />
      );

    case 'String':
    default:
      return (
        <Form.Control
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter value"
        />
      );
  }
}

export default FilterValueInput;
